import type { Timezone } from '@ipfabric/utils/timezones';
import {
  type ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { CronCustomFields } from './cronCustomFields';
import { EveryField } from './cronFields';
import { CronPresetFields } from './cronPresetFields';
import type { CronFormMeta } from './cronTypes';
import { useCronForm } from './useCronForm';

export type CronProps = {
  initialExpression: string;
  initialTimezone: Timezone;
  disabled?: boolean;
  onChange: (
    expression: string,
    timezone: Timezone,
    meta: CronFormMeta,
  ) => void;
};

export type CronRef = {
  /** Triggers validation on all visible cron fields, returning true if valid */
  triggerValidation: () => Promise<boolean>;
  resetValidation: () => void;
};

const BareCron = (
  { initialExpression, initialTimezone, disabled = false, onChange }: CronProps,
  ref: ForwardedRef<CronRef>,
) => {
  // Component is setup once and can't be controlled from outside
  const initialExpressionRef = useRef(initialExpression);
  const initialTimezoneRef = useRef(initialTimezone);

  const { form, every } = useCronForm(
    initialExpressionRef.current,
    initialTimezoneRef.current,
    onChange,
  );

  useImperativeHandle(
    ref,
    () => ({
      triggerValidation: () => form.trigger(),
      resetValidation: () => form.clearErrors(),
    }),
    [form],
  );

  return (
    <FormProvider {...form}>
      <div className='flex items-start gap-4'>
        <EveryField disabled={disabled} />

        {every !== 'custom' ? (
          <CronPresetFields everyValue={every} disabled={disabled} />
        ) : (
          <CronCustomFields disabled={disabled} />
        )}
      </div>
    </FormProvider>
  );
};

export const Cron = forwardRef(BareCron);
